import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    Plus,
    Download,
    Mail,
    Trash2,
    RefreshCw,
    TrendingUp,
    FileText,
    Package
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

// Components
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BulkContractCreateModal from '../../components/bulk-contracts/BulkContractCreateModal';
import BulkContractsList from '../../components/bulk-contracts/BulkContractsList';
import BulkAdditionalActModal from '../../components/bulk-contracts/BulkAdditionalActModal';
import BulkNoticeModal from '../../components/bulk-contracts/BulkNoticeModal';
import BulkEmailResultModal from '../../components/bulk-contracts/BulkEmailResultModal'; // ✨ NOU

// Services
import { bulkContractService } from '../../services/bulkContractService';
import { clientService } from '../../services/clientService';
import { serviceService } from '../../services/serviceService';
import { templateService } from '../../services/templateService';

const BulkContractsPage = () => {
    const queryClient = useQueryClient();

    // State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAdditionalActModal, setShowAdditionalActModal] = useState(false);
    const [showNoticeModal, setShowNoticeModal] = useState(false);
    const [selectedBulkEntry, setSelectedBulkEntry] = useState(null);
    const [expandedEntries, setExpandedEntries] = useState([]);
    
    // ✨ NOU: State pentru email result modal
    const [showEmailResultModal, setShowEmailResultModal] = useState(false);
    const [emailResult, setEmailResult] = useState(null);

    // Fetch data (același cod ca înainte)
    const {
        data: bulkEntries = [],
        isLoading: bulkEntriesLoading,
        refetch: refetchBulkEntries
    } = useQuery(
        'bulkEntries',
        bulkContractService.getAllBulkEntries,
        {
            onError: (error) => {
                console.error('Failed to load bulk entries:', error);
                toast.error('Eroare la încărcarea bulk entries');
            }
        }
    );

    const {
        data: clients = [],
        isLoading: clientsLoading
    } = useQuery('clients', clientService.getAll);

    const {
        data: services = [],
        isLoading: servicesLoading
    } = useQuery('services', serviceService.getAll);

    const {
        data: templates = [],
        isLoading: templatesLoading
    } = useQuery('templates', templateService.getActive);

    const {
        data: statistics,
        isLoading: statisticsLoading
    } = useQuery('bulkStatistics', bulkContractService.getBulkStatistics);

    // Mutations (majoritatea rămân la fel, doar modificăm cele de email)
    const deleteMutation = useMutation(
        bulkContractService.deleteBulkEntry,
        {
            onSuccess: () => {
                queryClient.invalidateQueries('bulkEntries');
                toast.success('Bulk entry șters cu succes');
            },
            onError: (error) => {
                console.error('Failed to delete bulk entry:', error);
                toast.error(error.response?.data?.message || 'Eroare la ștergerea bulk entry');
            }
        }
    );

    const downloadMutation = useMutation(
        bulkContractService.downloadBulkContracts,
        {
            onSuccess: ({ blob, filename }) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('Descărcare completă');
            },
            onError: (error) => {
                console.error('Failed to download bulk contracts:', error);
                toast.error('Eroare la descărcarea contractelor');
            }
        }
    );

    // ✨ MODIFICAT: Send emails mutation cu rezultate
    const sendEmailsMutation = useMutation(
        (bulkEntryId) => bulkContractService.sendBulkContractEmails(bulkEntryId, null),
        {
            onMutate: (bulkEntryId) => {
                toast.loading('Trimitere email-uri în curs...');
                console.log(bulkEntryId);
            },
            onSuccess: (result) => {
                // Afișează modal cu rezultate
                setEmailResult(result);
                setShowEmailResultModal(true);

                // Toast bazat pe rezultat
                if (result.isFullSuccess) {
                    toast.success(`Toate cele ${result.successCount} email-uri au fost trimise cu succes!`);
                } else if (result.hasIssues) {
                    toast.warning(
                        `${result.successCount} email-uri trimise. ${result.failedCount} erori, ${result.skippedCount} sărite.`,
                        { duration: 5000 }
                    );
                }
            },
            onError: (error) => {
                console.error('Failed to send bulk emails:', error);
                toast.error(error.response?.data?.message || 'Eroare la trimiterea email-urilor');
            }
        }
    );

    const downloadBulkAdditionalActsMutation = useMutation(
        bulkContractService.downloadBulkAdditionalActs,
        {
            onSuccess: ({ blob, filename }) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('Acte adiționale descărcate cu succes');
            },
            onError: (error) => {
                console.error('Failed to download bulk additional acts:', error);
                toast.error('Eroare la descărcarea actelor adiționale');
            }
        }
    );

    // ✨ MODIFICAT: Send bulk additional act emails cu rezultate
    const sendBulkAdditionalActEmailsMutation = useMutation(
        (bulkAdditionalActId) => bulkContractService.sendBulkAdditionalActEmails(bulkAdditionalActId, null),
        {
            onSuccess: (result) => {
                // Afișează modal cu rezultate
                setEmailResult(result);
                setShowEmailResultModal(true);

                // Toast bazat pe rezultat
                if (result.isFullSuccess) {
                    toast.success(`Toate cele ${result.successCount} email-uri au fost trimise cu succes!`);
                } else if (result.hasIssues) {
                    toast.warning(result.message, { duration: 5000 });
                }
            },
            onError: (error) => {
                console.error('Failed to send bulk additional act emails:', error);
                toast.error('Eroare la trimiterea email-urilor');
            }
        }
    );

    const downloadBulkNoticesMutation = useMutation(
        bulkContractService.downloadBulkNotices,
        {
            onSuccess: ({ blob, filename }) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('Notificări descărcate cu succes');
            },
            onError: (error) => {
                console.error('Failed to download bulk notices:', error);
                toast.error('Eroare la descărcarea notificărilor');
            }
        }
    );

    // ✨ MODIFICAT: Send bulk notice emails cu rezultate
    const sendBulkNoticeEmailsMutation = useMutation(
        (bulkNoticeId) => bulkContractService.sendBulkNoticeEmails(bulkNoticeId, null),
        {
            onSuccess: (result) => {
                // Afișează modal cu rezultate
                setEmailResult(result);
                setShowEmailResultModal(true);

                // Toast bazat pe rezultat
                if (result.isFullSuccess) {
                    toast.success(`Toate cele ${result.successCount} email-uri au fost trimise cu succes!`);
                } else if (result.hasIssues) {
                    toast.warning(result.message, { duration: 5000 });
                }
            },
            onError: (error) => {
                console.error('Failed to send bulk notice emails:', error);
                toast.error('Eroare la trimiterea email-urilor');
            }
        }
    );

    const generatePdfsMutation = useMutation(
        bulkContractService.generateBulkPdfs,
        {
            onSuccess: (result) => {
                queryClient.invalidateQueries('bulkEntries');

                if (result.failedCount === 0) {
                    toast.success(`Toate PDF-urile au fost generate cu succes (${result.successCount}/${result.totalContracts})`);
                } else {
                    toast.warning(
                        `${result.successCount} PDF-uri generate cu succes, ${result.failedCount} eșuate`,
                        { duration: 5000 }
                    );

                    result.failedContracts.forEach(failed => {
                        console.error(`Failed to generate PDF for contract ${failed.contractNumber}:`, failed.errorMessage);
                    });
                }
            },
            onError: (error) => {
                console.error('Failed to generate bulk PDFs:', error);
                toast.error('Eroare la generarea PDF-urilor');
            }
        }
    );

    const generateAdditionalActPdfsMutation = useMutation(
        bulkContractService.generateBulkAdditionalActPdfs,
        {
            onSuccess: (result) => {
                queryClient.invalidateQueries('bulkEntries');

                if (result.failedCount === 0) {
                    toast.success(`Toate PDF-urile actelor adiționale au fost generate cu succes (${result.successCount}/${result.totalContracts})`);
                } else {
                    toast(
                        `${result.successCount} PDF-uri generate cu succes, ${result.failedCount} eșuate`,
                        {
                            duration: 5000,
                            icon: '⚠️',
                            style: {
                                background: '#FEF3C7',
                                color: '#92400E',
                                border: '1px solid #FDE68A'
                            }
                        }
                    );

                    if (result.failedContracts?.length > 0) {
                        console.error('Failed to generate PDFs for:', result.failedContracts);
                    }
                }
            },
            onError: (error) => {
                console.error('Failed to generate bulk additional act PDFs:', error);
                toast.error('Eroare la generarea PDF-urilor pentru acte adiționale');
            }
        }
    );

    const generateNoticePdfsMutation = useMutation(
        bulkContractService.generateBulkNoticePdfs,
        {
            onSuccess: (result) => {
                queryClient.invalidateQueries('bulkEntries');

                if (result.failedCount === 0) {
                    toast.success(`Toate PDF-urile notificărilor au fost generate cu succes (${result.successCount}/${result.totalContracts})`);
                } else {
                    toast(
                        `${result.successCount} PDF-uri generate cu succes, ${result.failedCount} eșuate`,
                        {
                            duration: 5000,
                            icon: '⚠️',
                            style: {
                                background: '#FEF3C7',
                                color: '#92400E',
                                border: '1px solid #FDE68A'
                            }
                        }
                    );

                    if (result.failedContracts?.length > 0) {
                        console.error('Failed to generate PDFs for:', result.failedContracts);
                    }
                }
            },
            onError: (error) => {
                console.error('Failed to generate bulk notice PDFs:', error);
                toast.error('Eroare la generarea PDF-urilor pentru notificări');
            }
        }
    );
    
    // Handlers
    const handleToggleExpand = (entryId) => {
        setExpandedEntries(prev =>
            prev.includes(entryId)
                ? prev.filter(id => id !== entryId)
                : [...prev, entryId]
        );
    };

    const handleDelete = async (entryId) => {
        if (window.confirm('Sigur doriți să ștergeți acest bulk entry? Această acțiune nu poate fi anulată.')) {
            await deleteMutation.mutateAsync(entryId);
        }
    };

    const handleDownload = async (entryId) => {
        await downloadMutation.mutateAsync(entryId);
    };

    const handleSendEmails = async (entryId) => {
        if (window.confirm('Sigur doriți să trimiteți email-uri pentru toate contractele din acest bulk?')) {
            await sendEmailsMutation.mutateAsync(entryId);
        }
    };

    const handleCreateAdditionalActs = (bulkEntry) => {
        setSelectedBulkEntry(bulkEntry);
        setShowAdditionalActModal(true);
    };

    const handleCreateNotices = (bulkEntry) => {
        setSelectedBulkEntry(bulkEntry);
        setShowNoticeModal(true);
    };

    const handleModalSuccess = () => {
        refetchBulkEntries();
        queryClient.invalidateQueries('bulkStatistics');
    };

    const handleDownloadBulkAdditionalActs = async (bulkAdditionalActId) => {
        await downloadBulkAdditionalActsMutation.mutateAsync(bulkAdditionalActId);
    };

    const handleSendBulkAdditionalActEmails = async (bulkAdditionalActId) => {
        if (window.confirm('Sigur doriți să trimiteți email-uri pentru toate actele adiționale din acest bulk?')) {
            await sendBulkAdditionalActEmailsMutation.mutateAsync(bulkAdditionalActId);
        }
    };

    const handleDownloadBulkNotices = async (bulkNoticeId) => {
        await downloadBulkNoticesMutation.mutateAsync(bulkNoticeId);
    };

    const handleSendBulkNoticeEmails = async (bulkNoticeId) => {
        if (window.confirm('Sigur doriți să trimiteți email-uri pentru toate notificările din acest bulk?')) {
            await sendBulkNoticeEmailsMutation.mutateAsync(bulkNoticeId);
        }
    };

    const handleGeneratePdfs = async (bulkEntryId) => {
        if (window.confirm('Sigur doriți să generați/regenerați PDF-urile pentru toate contractele din acest bulk?')) {
            await generatePdfsMutation.mutateAsync(bulkEntryId);
        }
    };

    const handleGenerateAdditionalActPdfs = async (bulkAdditionalActId) => {
        if (window.confirm('Sigur doriți să generați/regenerați PDF-urile pentru toate actele adiționale din acest bulk?')) {
            await generateAdditionalActPdfsMutation.mutateAsync(bulkAdditionalActId);
        }
    };

    const handleGenerateNoticePdfs = async (bulkNoticeId) => {
        if (window.confirm('Sigur doriți să generați/regenerați PDF-urile pentru toate notificările din acest bulk?')) {
            await generateNoticePdfsMutation.mutateAsync(bulkNoticeId);
        }
    };

    const isLoading = bulkEntriesLoading || clientsLoading || servicesLoading || templatesLoading;

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <LoadingSpinner size="lg" text="Se încarcă..." />
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Contracte în Bulk
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Gestionează operațiuni în lot pentru contracte, acte adiționale și notificări
                    </p>
                </div>

                <div className="flex items-center space-x-3">
                    <Button
                        variant="outline"
                        icon={RefreshCw}
                        onClick={() => refetchBulkEntries()}
                    >
                        Reîmprospătează
                    </Button>

                    <Button
                        variant="primary"
                        icon={Plus}
                        onClick={() => setShowCreateModal(true)}
                    >
                        Creare Bulk Nou
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        icon={Package}
                        iconColor="text-blue-600"
                        iconBg="bg-blue-100"
                        title="Total Bulk Entries"
                        value={statistics.totalBulkEntries}
                        subtitle={`${statistics.bulkEntriesThisMonth} luna aceasta`}
                    />

                    <StatCard
                        icon={FileText}
                        iconColor="text-green-600"
                        iconBg="bg-green-100"
                        title="Contracte Create"
                        value={statistics.totalContractsCreatedInBulk}
                        subtitle="în bulk"
                    />

                    <StatCard
                        icon={TrendingUp}
                        iconColor="text-purple-600"
                        iconBg="bg-purple-100"
                        title="Acte Adiționale"
                        value={statistics.totalBulkAdditionalActs}
                        subtitle="bulk operations"
                    />

                    <StatCard
                        icon={Mail}
                        iconColor="text-orange-600"
                        iconBg="bg-orange-100"
                        title="Notificări"
                        value={statistics.totalBulkNotices}
                        subtitle="bulk operations"
                    />
                </div>
            )}

            {/* Last Operation */}
            {statistics?.lastBulkOperationDate && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                        <span className="font-medium">Ultima operațiune bulk:</span>{' '}
                        {format(new Date(statistics.lastBulkOperationDate), "dd MMMM yyyy 'la' HH:mm", { locale: ro })}
                    </p>
                </div>
            )}

            {/* Bulk Entries List */}
            <BulkContractsList
                bulkEntries={bulkEntries}
                expandedEntries={expandedEntries}
                onToggleExpand={handleToggleExpand}
                onDownload={handleDownload}
                onSendEmails={handleSendEmails}
                onDelete={handleDelete}
                onCreateAdditionalActs={handleCreateAdditionalActs}
                onCreateNotices={handleCreateNotices}
                onDownloadBulkAdditionalActs={handleDownloadBulkAdditionalActs}
                onSendBulkAdditionalActEmails={handleSendBulkAdditionalActEmails}
                onDownloadBulkNotices={handleDownloadBulkNotices}
                onSendBulkNoticeEmails={handleSendBulkNoticeEmails}
                onGeneratePdfs={handleGeneratePdfs}
                onGenerateBulkAdditionalActPdfs={handleGenerateAdditionalActPdfs}
                onGenerateBulkNoticePdfs={handleGenerateNoticePdfs}
                loading={
                    deleteMutation.isLoading ||
                    downloadMutation.isLoading ||
                    sendEmailsMutation.isLoading ||
                    generatePdfsMutation.isLoading ||
                    generateAdditionalActPdfsMutation.isLoading ||
                    generateNoticePdfsMutation.isLoading ||
                    downloadBulkAdditionalActsMutation.isLoading ||
                    sendBulkAdditionalActEmailsMutation.isLoading ||
                    downloadBulkNoticesMutation.isLoading ||
                    sendBulkNoticeEmailsMutation.isLoading
                }
            />

            {/* Modals */}
            <BulkContractCreateModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleModalSuccess}
                clients={clients}
                services={services}
                templates={templates}
            />

            <BulkAdditionalActModal
                isOpen={showAdditionalActModal}
                onClose={() => {
                    setShowAdditionalActModal(false);
                    setSelectedBulkEntry(null);
                }}
                onSuccess={handleModalSuccess}
                bulkEntry={selectedBulkEntry}
            />

            <BulkNoticeModal
                isOpen={showNoticeModal}
                onClose={() => {
                    setShowNoticeModal(false);
                    setSelectedBulkEntry(null);
                }}
                onSuccess={handleModalSuccess}
                bulkEntry={selectedBulkEntry}
            />

            {/* ✨ NOU: Email Result Modal */}
            <BulkEmailResultModal
                isOpen={showEmailResultModal}
                onClose={() => {
                    setShowEmailResultModal(false);
                    setEmailResult(null);
                }}
                result={emailResult}
            />
        </div>
    );
};

// Statistics Card Component
const StatCard = ({ icon: Icon, iconColor, iconBg, title, value, subtitle }) => {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
                <div className={`p-3 rounded-lg ${iconBg}`}>
                    <Icon className={`h-6 w-6 ${iconColor}`} />
                </div>
                <div className="ml-4 flex-1">
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                    {subtitle && (
                        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkContractsPage;